/**
 * ColorModeSelector - UI component for switching between light/dark/system modes
 * Uses shadcn/ui RadioGroup for proper radio semantics and accessibility
 */

import { useColorMode, type ColorMode } from '@/hooks/useColorMode';
import { cn } from '@/lib/utils';
import { Sun, Moon, Monitor, Check } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';

interface ColorModeSelectorProps {
  className?: string;
}

const modeOptions: {
  id: ColorMode;
  label: string;
  icon: typeof Sun;
  description: string;
}[] = [
  {
    id: 'light',
    label: 'Light',
    icon: Sun,
    description: 'Always use light mode',
  },
  {
    id: 'dark',
    label: 'Dark',
    icon: Moon,
    description: 'Always use dark mode',
  },
  {
    id: 'system',
    label: 'System',
    icon: Monitor,
    description: 'Match system preference',
  },
];

export function ColorModeSelector({ className }: ColorModeSelectorProps) {
  const { colorMode, setColorMode } = useColorMode();

  const handleModeChange = (mode: ColorMode) => {
    if (mode !== colorMode) {
      setColorMode(mode);
    }
  };

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-center gap-2 px-2 text-sm font-medium text-muted-foreground">
        <Sun className="h-4 w-4" />
        <span>Appearance</span>
      </div>
      <RadioGroup
        value={colorMode}
        onValueChange={() => {
          /* Handled by label click for animation coordinates */
        }}
        className="grid grid-cols-1 gap-2"
      >
        {modeOptions.map((option) => {
          const isSelected = colorMode === option.id;
          const Icon = option.icon;

          return (
            <Label
              key={option.id}
              htmlFor={`color-mode-${option.id}`}
              onClick={() => handleModeChange(option.id)}
              className={cn(
                'group relative flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-all',
                'hover:border-primary/50',
                'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary',
                isSelected ? 'border-primary bg-primary/5' : 'border-border',
              )}
            >
              <RadioGroupItem
                value={option.id}
                id={`color-mode-${option.id}`}
                className="sr-only"
              />
              <Icon
                className={cn(
                  'h-5 w-5',
                  isSelected ? 'text-primary' : 'text-muted-foreground',
                )}
              />
              <div className="flex-1">
                <span
                  className={cn(
                    'font-medium',
                    isSelected ? 'text-foreground' : 'text-foreground/80',
                  )}
                >
                  {option.label}
                </span>
                <p className="text-xs text-muted-foreground font-normal">
                  {option.description}
                </p>
              </div>
              {isSelected && <Check className="h-4 w-4 text-primary" />}
            </Label>
          );
        })}
      </RadioGroup>
    </div>
  );
}
